<div class="fcm-wrap">

  <!-- ─────────── Loading ─────────── -->
  <div *ngIf="loading" class="text-center py-5" aria-live="polite" aria-busy="true">
    <div class="spinner-border" role="status" aria-hidden="true"></div>
    <div class="mt-3">Loading your questionnaire…</div>
  </div>

  <!-- ─────────── PROGRESS BAR ─────────── -->
  <div class="fcm-progress" *ngIf="!loading && isInForm()">
    <ng-container *ngFor="let n of contentSectionNumbers(); let last = last; let i = index; trackBy: trackByIdentity">
      <button
        type="button"
        class="step-dot"
        [class.active]="isSectionActive(n)"
        [class.done]="isSectionDone(n)"
        (click)="jumpToSection(n)"
        [attr.data-testid]="tids.progress.dotPrefix + i"
        [attr.aria-label]="'Section ' + n + (isSectionActive(n) ? ' (current)' : isSectionDone(n) ? ' (completed)' : '')">
        <ng-container *ngIf="!isSectionDone(n)">{{ n }}</ng-container>
        <ng-container *ngIf="isSectionDone(n)">✓</ng-container>
      </button>
      <span class="step-line" *ngIf="!last"></span>
    </ng-container>
    <span class="step-line"></span>
    <span class="step-dot done-marker" aria-hidden="true">★</span>
  </div>

  <form [formGroup]="form" (ngSubmit)="$event.preventDefault()" novalidate>

    <!-- ─────────── INTRO ─────────── -->
    <section *ngIf="!loading && step === 'intro'" class="fcm-section">
      <div class="section-header">
        <div class="section-eyebrow">Let's begin</div>
        <h1 class="section-title">FCM Family Philanthropy Purpose Questionnaire</h1>
        <p class="section-desc">
          A short reflection that helps your advisor craft a personalized giving purpose
          statement for you and your family. You can return and update your answers any time.
        </p>
      </div>

      <fieldset class="mb-4">
        <legend class="q-label">Are you completing this on your own or as a couple?</legend>
        <div class="check-group">
          <label class="check-item" *ngFor="let t of availableSubmissionTypes()">
            <input type="radio" name="submissionType" [value]="t"
                   [(ngModel)]="submissionType" [ngModelOptions]="{ standalone: true }"
                   [attr.data-testid]="'fcm.intro.submission-' + t">
            <span>{{ labelForSubmissionType(t) }}</span>
          </label>
        </div>
      </fieldset>

      <div class="d-flex justify-content-end">
        <button type="button" class="btn-fcm btn-fcm-primary" (click)="startQuestionnaire()"
                [attr.data-testid]="tids.intro.beginBtn">
          Begin <span aria-hidden="true">→</span>
        </button>
      </div>
    </section>

    <!-- ─────────── CONTENT SECTIONS (driven by payload) ─────────── -->
    <ng-container *ngFor="let n of contentSectionNumbers(); trackBy: trackByIdentity">
      <section *ngIf="!loading && step === stepIdForSection(n)" class="fcm-section">
        <ng-container *ngIf="metaForSection(n) as meta">
          <div class="section-header">
            <div class="section-eyebrow">{{ meta.eyebrow }}</div>
            <h1 class="section-title">{{ meta.title }}</h1>
            <p class="section-desc">{{ meta.description }}</p>
          </div>
        </ng-container>

        <!-- Mode toggle (only if section has any madlib questions) -->
        <div class="mode-toggle" *ngIf="madlibQuestionsFor(n).length > 0">
          <button type="button" class="mode-btn"
                  [class.active]="modeForSection(n) === 'guided'"
                  (click)="setSectionMode(n, 'guided')"
                  [attr.data-testid]="tids.modeToggle.buttonPrefix + n + '-guided'">
            Guided questions
          </button>
          <button type="button" class="mode-btn"
                  [class.active]="modeForSection(n) === 'madlibs'"
                  (click)="setSectionMode(n, 'madlibs')"
                  [attr.data-testid]="tids.modeToggle.buttonPrefix + n + '-madlibs'">
            Fill-in-the-blank
          </button>
        </div>

        <!--
          GUIDED MODE — inline rendering (NOT via ngTemplateOutlet).

          Angular's `formControlName` cannot find the parent `formGroup`
          when the form control is inside a template rendered with
          ngTemplateOutlet, so we render the question switch directly
          inside this view to keep reactive-forms bindings working.
        -->
        <ng-container *ngIf="modeForSection(n) === 'guided'">
          <div class="q-block"
               *ngFor="let q of guidedQuestionsFor(n); trackBy: trackByQuestionId"
               [class.q-invalid]="shouldShowError(q)"
               [ngSwitch]="q.question_type">

            <!-- TEXTAREA -->
            <ng-container *ngSwitchCase="'textarea'">
              <label class="q-label" [attr.for]="'fcm-' + q.question_code">
                {{ q.label }}
              </label>
              <div class="q-hint" *ngIf="q.hint">{{ q.hint }}</div>
              <textarea [id]="'fcm-' + q.question_code"
                        [formControlName]="q.question_code"
                        rows="4"
                        placeholder="Share what comes to mind..."
                        [attr.data-testid]="tids.questionPrefix + q.question_code"></textarea>
            </ng-container>

            <!-- RADIO -->
            <ng-container *ngSwitchCase="'radio'">
              <div class="q-label">{{ q.label }}</div>
              <div class="q-hint" *ngIf="q.hint">{{ q.hint }}</div>
              <div class="check-group">
                <label class="check-item" *ngFor="let opt of q.answer_options">
                  <input type="radio"
                         [formControlName]="q.question_code"
                         [value]="opt.value"
                         [attr.data-testid]="tids.questionPrefix + q.question_code + '.' + opt.value">
                  <span>{{ opt.label }}</span>
                </label>
              </div>
            </ng-container>

            <!-- CHECKBOX (multi-select) -->
            <ng-container *ngSwitchCase="'checkbox'">
              <div class="q-label">
                {{ q.label }}
                <span class="text-muted small" *ngIf="q.question_code === 'core_values'">
                  ({{ selectedCountFor(q.question_code) }}/{{ maxCoreValues }})
                </span>
              </div>
              <div class="q-hint" *ngIf="q.hint">{{ q.hint }}</div>
              <div class="check-grid">
                <label class="check-item" *ngFor="let opt of q.answer_options">
                  <input type="checkbox"
                         [checked]="isChecked(q.question_code, opt.value)"
                         (change)="toggleCheckbox(q.question_code, opt.value, q.question_code === 'core_values' ? maxCoreValues : undefined)"
                         [attr.data-testid]="tids.questionPrefix + q.question_code + '.' + opt.value">
                  <span>{{ opt.label }}</span>
                </label>
              </div>
            </ng-container>

            <!-- VEHICLE CARD -->
            <ng-container *ngSwitchCase="'vehicle_card'">
              <div class="vehicle-card">
                <div class="vehicle-info">
                  <div class="vehicle-name">{{ q.label }}</div>
                  <div class="vehicle-desc" *ngIf="q.hint">{{ q.hint }}</div>
                </div>
                <div class="vehicle-btns">
                  <button type="button" class="vehicle-btn"
                          *ngFor="let opt of q.answer_options"
                          [class.sel-using]="opt.value === 'using' && vehicleValue(q.question_code) === 'using'"
                          [class.sel-curious]="opt.value === 'curious' && vehicleValue(q.question_code) === 'curious'"
                          [class.sel-neither]="opt.value === 'neither' && vehicleValue(q.question_code) === 'neither'"
                          (click)="selectVehicle(q.question_code, opt.value)"
                          [attr.data-testid]="tids.questionPrefix + q.question_code + '.' + opt.value">
                    {{ opt.label }}
                  </button>
                </div>
              </div>
            </ng-container>

            <!-- SLIDER (1-5) -->
            <ng-container *ngSwitchCase="'slider'">
              <div class="q-label">{{ q.label }}</div>
              <div class="q-hint" *ngIf="q.hint">{{ q.hint }}</div>
              <div class="tax-slider">
                <span class="tax-end-label">1</span>
                <input type="range" min="1" max="5" step="1"
                       [formControlName]="q.question_code"
                       [attr.data-testid]="tids.questionPrefix + q.question_code"
                       [attr.aria-label]="q.label">
                <span class="tax-end-label">5</span>
                <span class="tax-value">{{ form.get(q.question_code)?.value }}</span>
              </div>
            </ng-container>

            <div class="q-error" *ngIf="shouldShowError(q)" role="alert">
              This question is required.
            </div>

          </div>
        </ng-container>

        <!-- MADLIBS MODE -->
        <div *ngIf="modeForSection(n) === 'madlibs'" class="madlibs-box">
          <ng-container *ngFor="let q of madlibQuestionsFor(n); trackBy: trackByQuestionId">
            <ng-container *ngIf="splitMadlib(q.madlib_template) as parts">
              <div class="madlib-line" [class.q-invalid]="shouldShowError(q)">
                <span>{{ parts.before }}</span>
                <input type="text" class="ml-input"
                       [class.ml-input-invalid]="shouldShowError(q)"
                       [formControlName]="q.question_code"
                       [placeholder]="q.label"
                       [attr.data-testid]="tids.questionPrefix + q.question_code"
                       [attr.aria-label]="q.label" />
                <span>{{ parts.after }}</span>
              </div>
            </ng-container>
          </ng-container>
          <div class="q-error madlibs-error"
               *ngIf="sectionTouched[n] && !isSectionComplete(n)"
               role="alert">
            Please fill in every blank above.
          </div>
        </div>
      </section>
    </ng-container>

    <!-- ─────────── INTERSTITIAL (Section 6) ─────────── -->
    <section *ngIf="!loading && step === 'interstitial'" class="fcm-section">
      <div class="interstitial" *ngIf="interstitialQuestion() as iq">
        <div class="interstitial-eyebrow">Almost there</div>
        <div class="interstitial-title">{{ iq.label }}</div>
        <div class="interstitial-desc" *ngIf="iq.hint">{{ iq.hint }}</div>
        <textarea rows="5"
                  [formControlName]="iq.question_code"
                  placeholder="Share anything else on your mind — or leave blank and we'll work with what you've shared..."
                  [attr.data-testid]="tids.questionPrefix + iq.question_code"></textarea>
      </div>

      <div class="d-flex flex-wrap align-items-center gap-3 mt-3">
        <button type="button" class="btn-fcm btn-fcm-primary"
                (click)="generate()" [disabled]="submitting"
                [attr.data-testid]="tids.interstitial.generateBtn">
          <span *ngIf="!submitting">Generate my statement <span aria-hidden="true">→</span></span>
          <span *ngIf="submitting">Generating…</span>
        </button>
        <button type="button" class="interstitial-skip"
                (click)="generate()" [disabled]="submitting"
                [attr.data-testid]="tids.interstitial.skipBtn">
          Skip and generate now
        </button>
      </div>
    </section>

    <!-- ─────────── NAV (back/next) ─────────── -->
    <div class="nav-row" *ngIf="isInForm()">
      <button type="button" class="btn-fcm btn-fcm-outline"
              [class.invisible]="currentStepNumber() <= 1"
              (click)="back()"
              [attr.data-testid]="tids.nav.backBtn">
        <span aria-hidden="true">←</span> Back
      </button>
      <span class="counter">Step {{ currentStepNumber() }} of {{ totalContentSections() }}</span>
      <button type="button" class="btn-fcm btn-fcm-primary"
              (click)="next()"
              [attr.data-testid]="tids.nav.nextBtn">
        Next <span aria-hidden="true">→</span>
      </button>
    </div>
  </form>

  <!--
    ─────────── THANK YOU ───────────
    Shown after a successful POST. Mirrors the layout of
    `get-started/signup-complete/signup-complete.component.html`: advisor
    avatar card on top, big heading + message in the middle, single
    "Go to Dashboard" CTA at the bottom.
  -->
  <div *ngIf="!loading && step === 'thank-you'" class="container pt-4">
    <div class="ques-block-row" *ngIf="advisor.fullname">
      <div class="ques-block-inr m-auto ques-block-inr-width">
        <img src="assets/images/avatar.png" class="avatar-hexa" alt="Your advisor" />
        <div class="text-center font-16 mb-1">{{ advisor.fullname }}</div>
        <a class="text-break color-999 font-weight-300">{{ advisor.email }}</a>
      </div>
    </div>
    <div class="color-999 text-start width-80 mt-5 mb-5 sub-txt-big">
      <div class="font-30">Thank You!</div>
      <br />
      <p class="font-16">
        Thank you for completing the FCM Family Philanthropy Purpose Questionnaire. Your
        responses have been saved and your advisor will be in touch with your personalized
        giving purpose statement. Please press the Go to Dashboard button below to return
        to your dashboard.
      </p>
    </div>
    <div class="text-center mb-5">
      <a class="btn btn-primary btn-lg rounded-pill fw-light font-18"
         (click)="goToDashboard()"
         data-testid="fcm.thank-you.goto-dashboard-btn">
        Go to Dashboard
      </a>
    </div>
  </div>

  <!-- ─────────── GENERATING (async statement) ─────────── -->
  <!--
    Submit returns 202 { status: 'generating', submission_id }. The
    statement is produced asynchronously and the result is signalled via
    the per-user Pusher channel. We park here showing a spinner until the
    event arrives (or the ~90s fallback fires).
  -->
  <section *ngIf="!loading && step === 'generating'" class="fcm-section text-center"
           aria-live="polite" aria-busy="true">
    <div class="section-header">
      <div class="section-eyebrow">Generating your statement</div>
      <h1 class="section-title">We're preparing your purpose statement.</h1>
      <p class="section-desc">
        This usually takes less than a minute. Please keep this page open while
        we generate your personalized giving purpose statement.
      </p>
    </div>
    <div class="text-center py-4" [attr.data-testid]="tids.generating.spinner">
      <div class="spinner-border" role="status" aria-hidden="true"></div>
    </div>
  </section>

  <!-- ─────────── COUPLE WAITING ─────────── -->
  <section *ngIf="!loading && step === 'waiting'" class="fcm-section text-center">
    <div class="section-header">
      <div class="section-eyebrow">Thank you!</div>
      <h1 class="section-title">Your answers have been saved.</h1>
      <p class="section-desc">
        Once your partner completes the questionnaire, a combined giving statement will be generated.
      </p>
    </div>
    <button type="button" class="btn-fcm btn-fcm-primary" (click)="goToDashboard()"
            [attr.data-testid]="tids.waiting.backToDashboardBtn">
      Back to dashboard
    </button>
  </section>

  <!-- ─────────── GENERATION FAILED ─────────── -->
  <section *ngIf="!loading && step === 'failed'" class="fcm-section text-center">
    <div class="section-header">
      <div class="section-eyebrow">Something went wrong</div>
      <h1 class="section-title">We couldn't generate your statement.</h1>
      <p class="section-desc" [attr.data-testid]="tids.failed.message">
        We had trouble generating your giving purpose statement. Please return
        to the questionnaire and submit it again.
      </p>
    </div>
    <div class="d-flex flex-wrap justify-content-center gap-3">
      <button type="button" class="btn-fcm btn-fcm-primary" (click)="retrySubmit()"
              [attr.data-testid]="tids.failed.retryBtn">
        Return to questionnaire
      </button>
      <button type="button" class="btn-fcm btn-fcm-outline" (click)="goToDashboard()">
        Back to dashboard
      </button>
    </div>
  </section>

  <!-- ─────────── RESULT ─────────── -->
  <section *ngIf="!loading && step === 'result' && record" class="fcm-section">
    <div class="section-header">
      <div class="section-eyebrow">Your philanthropic profile</div>
      <h1 class="section-title">Your purpose summary</h1>
      <p class="section-desc">
        Based on your responses, here is a draft of your philanthropic identity — a starting
        point for deeper conversation with your Forum advisor.
      </p>
    </div>

    <div class="ai-card" [attr.data-testid]="tids.result.statementBlock">
      <div class="ai-doc-header">
        <div class="ai-doc-title">Family Philanthropy Purpose Statement</div>
        <div class="ai-doc-subtitle">Prepared by Forum Financial</div>
      </div>
      <div class="ai-content">
        <ng-container *ngIf="record.purpose_statement; else statementPending">
          <div [innerHTML]="formattedStatementHtml"></div>
        </ng-container>
        <ng-template #statementPending>
          <p class="text-muted">
            Your answers have been saved. Your personalized giving purpose statement is
            being prepared and your advisor will share it with you shortly.
          </p>
        </ng-template>
      </div>
    </div>

    <!--
      Profile cards — populated from statementData.responses[].
      Core values + giving profile grid + vehicles + free-text notes.
    -->
    <div class="profile-cards" *ngIf="statementData">
      <div class="result-card" *ngIf="multiLabelsByCode('core_values').length">
        <div class="result-label">Core values identified</div>
        <div class="tags">
          <span class="tag" *ngFor="let v of multiLabelsByCode('core_values')">{{ v }}</span>
        </div>
      </div>

      <div class="result-card">
        <div class="result-label">Giving profile</div>
        <div class="profile-grid">
          <div class="profile-item"><b>Style</b>{{ labelByCode('philanthropic_style') }}</div>
          <div class="profile-item"><b>Geography</b>{{ labelByCode('geographic_focus') }}</div>
          <div class="profile-item"><b>Involvement</b>{{ labelByCode('involvement_level') }}</div>
          <div class="profile-item"><b>Continuity intent</b>{{ labelByCode('continuation') }}</div>
          <div class="profile-item"><b>Tax sensitivity</b>{{ taxScoreLabel() }}</div>
        </div>
      </div>

      <div class="result-card" *ngIf="vehicleLabels().length">
        <div class="result-label">Giving vehicles</div>
        <div class="tags">
          <span class="tag" *ngFor="let v of vehicleLabels()">{{ v }}</span>
        </div>
      </div>

      <div class="result-card" *ngIf="additionalNotesValue()">
        <div class="result-label">Notes from you</div>
        <div class="notes-text">{{ additionalNotesValue() }}</div>
      </div>
    </div>

    <!--
      Advisor raw view — hidden on screen, included in advisor PDF.
      Driven by `statementData.responses[]` (already labeled by the BE).
    -->
    <div class="advisor-raw">
      <div class="advisor-section" *ngFor="let s of advisorViewSections">
        <div class="advisor-section-title">{{ s.title }}</div>
        <div class="advisor-qa" *ngFor="let row of s.rows">
          <div class="advisor-q">{{ row.label }}</div>
          <div class="advisor-a">{{ row.value }}</div>
        </div>
      </div>
    </div>

    <div class="result-actions">
      <button type="button" class="btn-fcm btn-fcm-primary"
              (click)="printAs('client')"
              [attr.data-testid]="tids.result.pdfClientBtn">
        Save as PDF — Client
      </button>
      <button type="button" class="btn-fcm btn-fcm-primary"
              (click)="printAs('advisor')"
              [attr.data-testid]="tids.result.pdfAdvisorBtn">
        Save as PDF — Advisor
      </button>
      <button type="button" class="btn-fcm btn-fcm-outline"
              (click)="goToDashboard()"
              data-testid="fcm.result.goto-dashboard-btn">
        Go to Dashboard
      </button>
      <button type="button" class="btn-fcm btn-fcm-outline"
              (click)="retake()"
              [attr.data-testid]="tids.result.startOverBtn">
        Start over
      </button>
    </div>

    <!--
      Send-this-statement card — emails the rendered statement to the
      user and/or their advisor. Backend hits
      AppRestEndPoint.FCM.EMAIL_STATEMENT.
    -->
    <div class="email-card">
      <div class="email-card-title">Send this statement</div>

      <div class="email-row">
        <label class="email-check-label">
          <input type="checkbox"
                 [(ngModel)]="emailSendToSelf"
                 [ngModelOptions]="{ standalone: true }"
                 data-testid="fcm.result.send-self-checkbox">
          <span>Send to myself</span>
        </label>
      </div>

      <div class="email-row">
        <label class="email-check-label">
          <input type="checkbox"
                 [(ngModel)]="emailSendToAdvisor"
                 [ngModelOptions]="{ standalone: true }"
                 data-testid="fcm.result.send-advisor-checkbox">
          <span>Send to my Forum advisor</span>
        </label>
        <input type="email"
               class="email-input"
               placeholder="advisor@forumfinancial.com"
               [(ngModel)]="emailAdvisorAddress"
               [ngModelOptions]="{ standalone: true }"
               [disabled]="!emailSendToAdvisor"
               data-testid="fcm.result.send-advisor-input">
      </div>

      <div class="email-actions">
        <button type="button"
                class="btn-fcm btn-fcm-primary"
                (click)="sendStatement()"
                [disabled]="emailSending"
                data-testid="fcm.result.send-btn">
          <span *ngIf="!emailSending">Send</span>
          <span *ngIf="emailSending">Sending…</span>
        </button>
        <span *ngIf="emailStatus"
              class="email-status"
              [class.email-status-ok]="emailStatusOk"
              [class.email-status-error]="!emailStatusOk">
          {{ emailStatus }}
        </span>
      </div>
    </div>
  </section>
</div>

